import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, eur, fmtDate, useApi } from "@/lib/api";
import { StateBadge } from "@/components/club/StatusBadge";
import { PartnerForm, emptyPartner } from "@/components/partner/PartnerForm";
import { Btn, NoteDialog, Table, Td } from "./AdminUi";

function useAct(load) {
  return async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
}

export function AdminPartners() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => setRows(await api("get", "/admin/partners")), [api]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const act = useAct(load);
  const setStatus = (p, status, n = "") => act(() => api("post", `/admin/partners/${p.id}/status`, { status, note: n }), "Partner aggiornato");

  return (
    <div className="space-y-4">
      <Btn variant="gold" testid="admin-create-partner" onClick={() => setCreating(true)}>+ Crea azienda partner</Btn>
      <Table testid="admin-partners" empty={!rows.length} head={["Azienda", "Contatti", "IBAN / Spedizione", "Stato", "Azioni"]}>
        {rows.map((p) => (
          <tr key={p.id} data-testid={`admin-partner-${p.id}`}>
            <Td><p className="text-white">{p.company_name}</p><p className="text-xs text-slate-500">P.IVA {p.vat_number} · {fmtDate(p.created_at)}</p></Td>
            <Td><p>{p.contact_email}</p><p className="text-xs text-slate-500">{p.phone} · {p.address}</p></Td>
            <Td><p className="font-mono text-xs">{p.iban}</p><p className="text-xs text-slate-500">Sped. {eur(p.shipping_cost)} · {p.pickup ? "Ritiro sì" : "Ritiro no"}</p></Td>
            <Td><StateBadge status={p.status} />{p.note && <p className="mt-1 text-xs text-slate-500">{p.note}</p>}</Td>
            <Td>
              <div className="flex flex-wrap gap-2">
                {p.status !== "approved" && <Btn variant="ok" testid={`approve-partner-${p.id}`} onClick={() => setStatus(p, "approved")}>{p.status === "suspended" ? "Riattiva" : "Approva"}</Btn>}
                {p.status === "pending" && <Btn variant="warn" testid={`reject-partner-${p.id}`} onClick={() => setNote({ p, status: "rejected" })}>Rifiuta</Btn>}
                {p.status === "approved" && <Btn variant="warn" testid={`suspend-partner-${p.id}`} onClick={() => setNote({ p, status: "suspended" })}>Sospendi</Btn>}
              </div>
            </Td>
          </tr>
        ))}
      </Table>
      {note && <NoteDialog title={note.status === "rejected" ? "Rifiuta richiesta" : "Sospendi partner"} onClose={() => setNote(null)} onConfirm={(n) => setStatus(note.p, note.status, n)} />}
      {creating && (
        <Dialog open onOpenChange={(v) => !v && setCreating(false)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#12141C] sm:max-w-2xl">
            <DialogHeader><DialogTitle className="text-white">Nuova azienda partner</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">Il partner accederà registrandosi con l'email di contatto indicata.</p>
            <PartnerForm initial={emptyPartner} submitLabel="Crea partner" onSubmit={async (d) => { await act(() => api("post", "/admin/partners", d), "Partner creato"); setCreating(false); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function AdminOffers() {
  const api = useApi();
  const [status, setStatusFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState(null);
  const load = useCallback(async () => setRows(await api("get", "/admin/offers", null, status === "all" ? {} : { status })), [api, status]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const act = useAct(load);
  const moderate = (o, s, n = "") => act(() => api("post", `/admin/offers/${o.id}/status`, { status: s, note: n }), "Offerta aggiornata");

  return (
    <div className="space-y-4">
      <Select value={status} onValueChange={setStatusFilter}>
        <SelectTrigger data-testid="admin-offers-filter" className="w-64 bg-[#12141C]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="pending">In attesa di approvazione</SelectItem>
          <SelectItem value="approved">Pubblicate</SelectItem>
          <SelectItem value="suspended">Sospese</SelectItem>
          <SelectItem value="rejected">Rifiutate</SelectItem>
          <SelectItem value="all">Tutte</SelectItem>
        </SelectContent>
      </Select>
      <Table testid="admin-offers" empty={!rows.length} head={["Offerta", "Partner", "Bronze", "Gold", "Diamond", "Stato", "Azioni"]}>
        {rows.map((o) => (
          <tr key={o.id} data-testid={`admin-offer-${o.id}`}>
            <Td><div className="flex gap-3">{o.image_url && <img src={o.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" />}<div><p className="text-white">{o.name}</p><p className="text-xs text-slate-500">{o.category} · stock {o.stock}</p><p className="line-clamp-2 max-w-xs text-xs text-slate-500">{o.description}</p></div></div></Td>
            <Td>{o.partner_name}</Td>
            <Td className="text-[#CD7F32]">{eur(o.price_bronze)}</Td>
            <Td className="text-amber-400">{eur(o.price_gold)}</Td>
            <Td className="text-sky-400">{eur(o.price_diamond)}</Td>
            <Td><StateBadge status={o.status} />{o.note && <p className="mt-1 text-xs text-slate-500">{o.note}</p>}</Td>
            <Td>
              <div className="flex flex-wrap gap-2">
                {o.status !== "approved" && <Btn variant="ok" testid={`approve-offer-${o.id}`} onClick={() => moderate(o, "approved")}>{o.status === "suspended" ? "Riattiva" : "Approva"}</Btn>}
                {o.status === "pending" && <Btn variant="warn" testid={`reject-offer-${o.id}`} onClick={() => setNote({ o, s: "rejected" })}>Rifiuta</Btn>}
                {o.status === "approved" && <Btn variant="warn" testid={`suspend-offer-${o.id}`} onClick={() => setNote({ o, s: "suspended" })}>Sospendi</Btn>}
                <Btn variant="warn" testid={`delete-offer-${o.id}`} onClick={() => window.confirm("Eliminare l'offerta?") && act(() => api("delete", `/admin/offers/${o.id}`), "Offerta eliminata")}>Elimina</Btn>
              </div>
            </Td>
          </tr>
        ))}
      </Table>
      {note && <NoteDialog title={note.s === "rejected" ? "Rifiuta offerta" : "Sospendi offerta"} onClose={() => setNote(null)} onConfirm={(n) => moderate(note.o, note.s, n)} />}
    </div>
  );
}
